/**
 * Load an attached file into Clarid (agent file loading): an HMDA LAR, a CRA loan register or the Branch &
 * LPO Template. The agent proposed it; a person clicks Load. The server records the approval, then this
 * browser sends the file to the same upload the file's own page uses (HMDA validation and edit checks, the
 * CRA register import, the branch template import). The server then confirms the load by the file's hash,
 * not by anything this page reports.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';
import { hmdaApi, craApi, fairLendingApi } from '../../lib/api';
import { agentApi, fileSha256, type ActionCardState } from '../../lib/agent';
import { shell, primaryBtn, secondaryBtn, useRefresh } from './cardKit';
import { useAgentFiles } from './agentFiles';

export default function LoadCard({ teamId, card, queryKey }: { teamId: string; card: ActionCardState; queryKey: unknown[] }) {
  const refresh = useRefresh(queryKey);
  const files = useAgentFiles();
  const picker = useRef<HTMLInputElement>(null);
  const [needFile, setNeedFile] = useState(false);
  const sha = card.params?.sha256 ?? '';
  const name = card.params?.fileName ?? 'the file';
  const warnings = card.params?.warnings ?? [];
  const fileType = card.params?.fileType ?? 'hmda_lar';
  const how = HOW[fileType];

  /** Send the file to the HMDA upload, then let the server confirm what happened, by hash. */
  const send = async (file: File) => {
    try {
      await how.upload(teamId, file);
    } catch {
      // The upload's own error is shown by the API layer; the server still records the outcome.
    }
    await agentApi.confirmLoad(teamId, card.id);
    await refresh();
  };

  const load = useMutation({
    mutationFn: async (file: File) => {
      if (card.status === 'PROPOSED') await agentApi.approveAction(teamId, card.id);
      await send(file);
    },
    onError: refresh,
  });
  const reject = useMutation({ mutationFn: () => agentApi.rejectAction(teamId, card.id), onSuccess: refresh });

  const start = () => {
    const file = files.get(sha);
    if (file) load.mutate(file);
    else {
      setNeedFile(true);
      picker.current?.click();
    }
  };

  const picked = async (file: File | undefined) => {
    if (!file) return;
    if ((await fileSha256(file)) !== sha) {
      toast.error(`That is not the same file as ${name}. Choose the file you attached to the conversation.`);
      return;
    }
    setNeedFile(false);
    load.mutate(file);
  };

  const busy = load.isPending || reject.isPending;
  return (
    <div className={clsx(shell, card.status === 'FAILED' ? 'border-red-200' : 'border-gray-200')}>
      <input ref={picker} type="file" className="hidden" onChange={(e) => picked(e.target.files?.[0])} />
      <div className="flex items-start gap-3">
        <DocumentArrowUpIcon className="mt-0.5 h-5 w-5 shrink-0 text-ocean-600" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-navy-900">{card.title}</p>

          {warnings.length > 0 && (card.status === 'PROPOSED' || card.status === 'AWAITING_UPLOAD') && (
            <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
              {warnings.map((w) => (
                <li key={w} className="flex items-start gap-1"><ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" /> {w}</li>
              ))}
            </ul>
          )}

          {(card.status === 'PROPOSED' || card.status === 'AWAITING_UPLOAD') && (
            <>
              <p className="mt-1 text-xs text-gray-500">
                {card.status === 'PROPOSED'
                  ? `${how.runsThrough} Your approval is recorded.`
                  : 'Approved. Send the file to finish loading it.'}
              </p>
              {needFile && <p className="mt-1 text-xs text-amber-700">Choose {name} again: files attached to a conversation are not kept after the page reloads.</p>}
              <div className="mt-2 flex gap-2">
                <button type="button" className={primaryBtn} disabled={busy} onClick={start}>
                  {load.isPending ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowUpTrayIcon className="h-4 w-4" />}
                  {load.isPending ? 'Loading and checking…' : card.status === 'PROPOSED' ? 'Load into Clarid' : 'Send the file'}
                </button>
                {card.status === 'PROPOSED' && (
                  <button type="button" className={secondaryBtn} disabled={busy} onClick={() => reject.mutate()}>Not now</button>
                )}
              </div>
            </>
          )}

          {card.status === 'DONE' && (
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircleIcon className="h-4 w-4" /> {how.loaded(card)}
              </span>
              <Link to={card.result?.link ?? how.link} className="font-medium text-ocean-700 hover:text-ocean-800">{how.open}</Link>
            </p>
          )}
          {card.status === 'FAILED' && (
            <p className="mt-1 inline-flex items-start gap-1 text-xs text-red-700">
              <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0" /> Not loaded: {card.error ?? 'the upload did not complete.'}
            </p>
          )}
          {card.status === 'REJECTED' && <p className="mt-1 text-xs text-gray-500">Not loaded.</p>}
        </div>
      </div>
    </div>
  );
}

const n = (v: number | undefined) => (typeof v === 'number' ? v.toLocaleString('en-US') : '');

/** Each loadable type: the existing upload it goes through and how its result reads. */
const HOW: Record<'hmda_lar' | 'cra_loan_register' | 'branch_list', {
  upload: (teamId: string, file: File) => Promise<unknown>;
  runsThrough: string;
  loaded: (card: ActionCardState) => string;
  link: string;
  open: string;
}> = {
  hmda_lar: {
    upload: (teamId, file) => hmdaApi.validate(file, teamId),
    runsThrough: "Runs through Clarid's HMDA validation and edit checks, exactly like uploading it on the LAR Validation page.",
    loaded: (c) => `Loaded${c.result?.year ? `: the ${c.result.year} HMDA LAR` : ''}${typeof c.result?.totalLoans === 'number' ? `, ${n(c.result.totalLoans)} loans` : ''}`,
    link: '/hmda/lar-validation',
    open: 'See the edit checks',
  },
  cra_loan_register: {
    upload: (teamId, file) => craApi.importSbLoans(teamId, file),
    runsThrough: "Runs through Clarid's CRA loan register import, exactly like importing it on the SB Loans page.",
    loaded: (c) => `Loaded: ${n(c.result?.rows)} loans in the CRA register`,
    link: '/cra/sb-loans',
    open: 'Open SB Loans',
  },
  branch_list: {
    upload: (teamId, file) => fairLendingApi.importWallaceTemplate(teamId, file),
    runsThrough: "Runs through Clarid's Branch & LPO Template import, exactly like importing it on the Branches page.",
    loaded: (c) => `Loaded: ${n(c.result?.rows)} active branches and offices`,
    link: '/branches',
    open: 'Open Branches',
  },
};
